import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useFormatters } from '../../shared/format/use-formatters';
import { usePlanPrices } from '../../shared/pricing/use-plan-prices';
import { legalResources } from './legal-resources';

const sectionIds = [
  'scope',
  'access',
  'use',
  'payments',
  'rights',
  'responsibility',
  'changes',
  'contact',
] as const;
export type LegalDocument = 'terms' | 'privacy' | 'responsible';

const numberOf = (index: number) => String(index + 1).padStart(2, '0');

const resourceLinkClass = 'text-sm text-accent-ink underline-offset-4 hover:underline';

export const LegalPage = ({ document }: { document: LegalDocument }) => {
  const { t } = useTranslation();
  const { formatCalendarDay } = useFormatters();
  const prices = usePlanPrices();
  const { locale } = useParams();
  const draft = document !== 'responsible';
  const updatedAt = draft ? '2026-09-09' : '2026-09-06';

  return (
    <article className="py-12 sm:py-20">
      <div className="max-w-3xl pb-12">
        <Link
          className="text-sm text-muted underline underline-offset-4 hover:text-accent-ink"
          to={`/${locale}/`}
        >
          {t('legal.back')}
        </Link>
        <h1 className="mt-8 text-4xl font-semibold tracking-tight text-content sm:text-5xl lg:text-6xl">
          {t(`legal.${document}.title`)}
        </h1>
        <p className="mt-6 max-w-[55ch] text-lg text-muted">{t(`legal.${document}.description`)}</p>
        <p className="mt-4 text-sm text-muted">
          {t('legal.updated')}{' '}
          <time dateTime={updatedAt}>{formatCalendarDay(updatedAt, 'LL')}</time>
        </p>
        {draft ? (
          <aside className="mt-8 rounded-xl border-l-2 border-accent bg-raised px-6 py-5 text-sm">
            <strong className="text-content">{t('legal.draftTitle')}</strong>
            <p className="mt-2 text-muted">{t('legal.draftDescription')}</p>
          </aside>
        ) : null}
      </div>
      <div className="grid gap-12 border-t border-line pt-12 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <nav
          className="flex flex-col gap-3 self-start lg:sticky lg:top-24"
          aria-label={t('legal.contents')}
        >
          <h2 className="text-sm font-semibold text-content">{t('legal.contents')}</h2>
          {sectionIds.map((id, index) => (
            <a
              key={id}
              href={`#${id}`}
              className="flex gap-3 text-sm text-muted hover:text-accent-ink"
            >
              <span className="text-accent-ink tabular-nums">{numberOf(index)}</span>
              {t(`legal.${document}.sections.${id}.title`)}
            </a>
          ))}
        </nav>
        <div>
          {sectionIds.map((id, index) => (
            <section
              key={id}
              id={id}
              className="scroll-mt-24 pb-10"
              aria-labelledby={`${id}-title`}
            >
              <h2
                id={`${id}-title`}
                className="flex items-baseline gap-4 text-2xl font-semibold tracking-tight text-content"
              >
                <span className="text-sm text-accent-ink tabular-nums">{numberOf(index)}</span>
                {t(`legal.${document}.sections.${id}.title`)}
              </h2>
              <p className="mt-4 max-w-[68ch] leading-relaxed whitespace-pre-line text-muted">
                {t(`legal.${document}.sections.${id}.body`, prices)}
              </p>
            </section>
          ))}
          <aside className="grid gap-3 border-t border-line pt-8">
            <h2 className="text-sm font-semibold text-content">{t('legal.resources')}</h2>
            {legalResources.map(({ label, href }) => (
              <a href={href} key={label} className={resourceLinkClass}>
                {t(`footer.${label}`)}
              </a>
            ))}
            {document === 'privacy' && (
              <a
                href="https://www.planalto.gov.br/ccivil_03/_ato2015-2018/2018/lei/l13709compilado.htm"
                className={resourceLinkClass}
              >
                Lei Geral de Proteção de Dados (LGPD)
              </a>
            )}
            {document === 'terms' && (
              <a
                href="https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2013/decreto/d7962.htm"
                className={resourceLinkClass}
              >
                Decreto nº 7.962/2013
              </a>
            )}
          </aside>
        </div>
      </div>
    </article>
  );
};
